import { useState } from 'react';
import { Box, Checkbox, Icon, Label, Modal, Select, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogComponentProps } from 'shared';
import { BankAccountRow, getMaskedAccountLabel } from 'shared/accountAndBilling';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import PadAgreementPopup from '@/components/SubAccounts/TransferFunds/PadAgreementPopup/PadAgreementPopup';
import { Button } from 'UI';
import styles from './AddOperationalDialog.module.scss';

const cn = withStyles(styles);

/** The two ways out of the dialogue: pick an account that is already connected, or connect a new one. */
export type AddOperationalSelection =
  | { source: 'existing'; token: string }
  | { source: 'connect'; paymentMethod: 'bank' | 'credit' };

export type AddOperationalDialogProps = DialogComponentProps<AddOperationalSelection> & {
  /**
   * Connected bank accounts that aren't operational accounts yet; with none, only the eLinx route is
   * offered. Bank accounts only — the page filters cards out, which is why there is no card branch.
   */
  selectableAccounts: BankAccountRow[];
};

// The one operational dialogue that returns a value rather than a decision, so it is the one opened
// through useDialog() instead of useAlertDialog(). It owns the selection, the payment-method type and
// the PAD acceptance, none of which is page state.
//
// No reset() on the way out and no hand-drawn chrome: shared's dialog provider unmounts its content on
// close and Dialog.Content supplies the card, the corner X, the focus trap and the Escape handler.
export default function AddOperationalDialog({ selectableAccounts, onConfirm, onCancel }: AddOperationalDialogProps) {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.AddOperationalDialog',
  });

  const { response } = useClientData();
  const { LockedSelectOptions } = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const [selectedToken, setSelectedToken] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bankAccount');
  const [isAgreeingToAgreement, setIsAgreeingToAgreement] = useState(false);
  const [isShowingPadAgreement, setIsShowingPadAgreement] = useState(false);

  const isFranchise = response.SubAccountType === 'franchise';

  function renderAccountOption(account: BankAccountRow) {
    return (
      <Box flx="row" ai="center">
        {account.BankId ? (
          <img
            alt={t('bankLogoAlt', { defaultValue: 'bank logo' })}
            className={cn('logos')}
            src={`${import.meta.env.VITE_EMBED_ASSETS_URL}/images/logos-banks/${account.BankId}.svg`}
          />
        ) : (
          <Icon icon="bankDark" size="sm" />
        )}

        {getMaskedAccountLabel(account)}
      </Box>
    );
  }

  return (
    <div data-testid="add-operational-dialog">
      <Dialog.Title className={cn('title')}>
        {t('addOperationalAccount', { defaultValue: 'Add Operational Account' })}
      </Dialog.Title>

      <Box flx="col" width="100%" mt="md">
        {selectableAccounts.length > 0 && (
          <>
            <Label isBold={false} label={t('selectAccount', { defaultValue: 'Select an account' })} required={false} />

            <Select
              version="2"
              options={selectableAccounts}
              optionValue={(account) => account.Token}
              optionLabel={getMaskedAccountLabel}
              name="operationalAccount"
              value={selectedToken}
              filterable
              placeholder={t('selectConnectedAccount', { defaultValue: 'Select connected account' })}
              sort={false}
              onChange={(e) => {
                setSelectedToken(e.target.value);
              }}
              renderOption={(account, { Option }) => <Option>{renderAccountOption(account)}</Option>}
              renderSelectedValue={(account, { Placeholder }) =>
                account ? renderAccountOption(account) : <Placeholder />
              }
            />

            <Box mt="md" jc="center">
              <Button
                id="addOperational-next"
                colorScheme="secondary"
                size="large"
                onClick={() => {
                  // Nothing picked yet: the legacy Next was likewise a no-op rather than an error.
                  if (selectedToken === '') return;

                  onConfirm({ source: 'existing', token: selectedToken });
                }}
              >
                {t('next', { defaultValue: 'Next' })}
              </Button>
            </Box>

            <div className={cn('lineContainer')}>
              <span>{t('or', { defaultValue: 'OR' })}</span>
            </div>
          </>
        )}

        <Label
          isBold={false}
          label={t('selectPaymentMethodType', { defaultValue: 'Select payment method type' })}
          required={false}
        />

        {/* Select v1: it is the only version with lockedValues / lockedCallback, and the one version
            whose onBlur is required. */}
        <Select
          options={{
            bankAccount: t('bankAccount', { defaultValue: 'Bank Account' }),
            ...(response.FundMyAccountCreditCardEnabled
              ? { creditCard: t('creditCard', { defaultValue: 'Credit Card' }) }
              : {}),
          }}
          name="paymentMethod"
          variant="outlined"
          lockedValues={LockedSelectOptions}
          lockedCallback={(lockedFeature: string) => {
            updateIsLocked(true, lockedFeature);
            onCancel();
          }}
          value={paymentMethod}
          placeholder={t('selectPaymentMethod', { defaultValue: 'Select payment method' })}
          selectionAreaHeight="200px"
          onBlur={() => {}}
          noOrdering
          onChange={(e) => {
            setPaymentMethod(e.target.value);
          }}
        />

        {isFranchise && (
          <Box gapSize="10px" mt="md" ai="fs">
            <div className={cn('checkbox')}>
              <Checkbox
                name="agreeToAgreement"
                labelPosition="right"
                value={isAgreeingToAgreement}
                variant="border"
                labelSize="16px"
                onChange={(_e, props) => {
                  setIsAgreeingToAgreement(!!props.isEnabled);
                }}
              />
            </div>

            <div className={cn('label')}>
              {t('agreeToTerms', {
                defaultValue: 'By checking this box I have read and agree to the terms of the',
              })}{' '}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setIsShowingPadAgreement(true);
                }}
                type="button"
                className={cn('link')}
              >
                {t('padAgreement', { defaultValue: 'Pre-Authorized Debit (PAD) Agreement' })}
              </button>
            </div>
          </Box>
        )}

        <Box mt="md" jc="center">
          <Button
            id="addOperational-addPaymentMethod"
            colorScheme="primary"
            size="large"
            disabled={isFranchise && !isAgreeingToAgreement}
            onClick={() => {
              onConfirm({ source: 'connect', paymentMethod: paymentMethod === 'creditCard' ? 'credit' : 'bank' });
            }}
          >
            {t('addPaymentMethod', { defaultValue: 'Add Payment Method' })}
          </Button>
        </Box>
      </Box>

      {/* The dialog provider holds one dialogue at a time, so opening the PAD agreement through it would
          replace this one and resolve its promise with null. It gets its own Modal instead. */}
      <Modal
        isVisible={isShowingPadAgreement}
        onBgClick={() => {
          setIsShowingPadAgreement(false);
        }}
      >
        <PadAgreementPopup
          onConfirm={() => {
            setIsShowingPadAgreement(false);
          }}
          onCancel={() => {
            setIsShowingPadAgreement(false);
          }}
        />
      </Modal>
    </div>
  );
}
